import React, { useCallback, useState, useEffect } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import {
  Modal, Table, Button, Tooltip, Spin,
} from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import Scrollbar from "react-scrollbars-custom";

import QcTooltipContent from "./QcTooltipContent";
import { BATCH_REPORT_GENERATE_STATUS, BATCH_REPORT_TEST_STATUS } from "../../../utils/statusConfig";
import { getWoInfo } from "../../../actions/batchReports";

import "./WoDetailsModal.css";

function getWoInfoFromData(data) {
  if (!data) return [];
  let assignedTo = "-";
  if (Array.isArray(data.technician_detail) && data.technician_detail.length) {
    assignedTo = data.technician_detail.map((item) => {
      // Handle both object format {technician: "...", date: "..."} and string format
      const technician = typeof item === "object" && item !== null ? (item.technician || item.name || item) : item;
      const date = typeof item === "object" && item !== null && item.process_on ? (moment(item.process_on, "YYYY-MM-DD").format("MM/DD/YYYY")) : null;
      return date ? `${technician} (${date})` : technician;
    });
  } else if (data.technician) {
    assignedTo = data.created_at ? `${data.technician} (${data.created_at})` : data.technician;
  }
  return [
    { label: "Workorder:", value: data.workorder_id || "-" },
    { label: "Created:", value: data.created_at || "-" },
    { label: "Customer ID:", value: data.company_id || "-" },
    { label: "PO:", value: data.po || "-" },
    { label: "Assigned To:", value: assignedTo },
    { label: "# of Samples:", value: data.total_sample || "-" },
  ];
}

/**
 * Map and order stages: Aliquot first, then SamplePrep (with technician in parenthesis), then rest.
 * Label formatting: "Aliquot (3)" or "Aliquot", "SamplePrep (Holden Samples)", etc.
 * Uses completed (WO stages) or marked (sample stages) for checked state.
 * Uses technician or size_group_info for label suffix.
 */
function mapAndOrderStages(stages) {
  if (!Array.isArray(stages) || stages.length === 0) return [];
  const mapped = stages.map((stage, index) => {
    const stageName = stage.stage_name ?? stage.stage ?? "";
    const stageNameLower = stageName.toLowerCase().trim();
    let suffix = null;
    if (stage.technician != null && stage.technician !== "") {
      suffix = stage.technician;
    } else if (stage.size_group_info != null && stage.size_group_info !== "") {
      suffix = stage.size_group_info;
    }
    let label = stageName;
    if (stageNameLower === "aliquot" || stageNameLower.includes("aliquot")) {
      label = suffix ? `Aliquot (${suffix})` : (stageName || "Aliquot");
    } else if (stageNameLower.includes("sampleprep") || stageNameLower.includes("sample prep")) {
      label = suffix ? `${stageName} (${suffix})` : stageName;
    } else if (suffix) {
      label = `${stageName} (${suffix})`;
    }
    return {
      label,
      checked: stage.completed === true || stage.marked === true,
      stageNameLower,
      originalIndex: index,
    };
  });
  const aliquot = mapped.filter((s) => s.stageNameLower === "aliquot" || s.stageNameLower.includes("aliquot"));
  const samplePrep = mapped.filter(
    (s) => (s.stageNameLower.includes("sampleprep") || s.stageNameLower.includes("sample prep")) && !s.stageNameLower.includes("aliquot"),
  );
  const rest = mapped.filter(
    (s) => s.stageNameLower !== "aliquot" && !s.stageNameLower.includes("aliquot") && !s.stageNameLower.includes("sampleprep") && !s.stageNameLower.includes("sample prep"),
  );
  return [...aliquot, ...samplePrep, ...rest].map(({ stageNameLower, originalIndex, ...restProps }) => restProps);
}

function getTableDataSourceFromData(data) {
  if (!data?.sample?.length) return [];
  return data.sample.map((row) => ({
    key: String(row.id),
    id: row.id,
    genista_id: row.genista_id ?? "-",
    sample_id: row.sample_id ?? "-",
    sample_type: row.sample_type ?? "-",
    analysis: row.test ?? "-",
    test_category: row.test_category ?? "-",
    test_status: row.test_status ?? row.status ?? "-",
    result: row.result ?? "-",
    uploadedDate: row.result_updated_at ?? "-",
    resultUploadedBy: row.result_updated_by ?? "-",
    belongs_to: row.belongs_to ?? "-",
    notes: row.notes ?? "-",
    composite: row.composite_number ?? "-",
    petrifilm: row.petrifilm ?? "-",
    plate: row.plate ?? "-",
    ...row,
  }));
}

export default function WoDetailsModal({ workorderPk, genistaId = null, closeModal }) {
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [woData, setWoData] = useState(null);
  const additionalInformation = [
    { label: "Agar Batch", field: "agar_batch" },
    { label: "BPW Batch", field: "bpw_batch_number" },
    { label: "Carboy", field: "carboy_number" },
    { label: "Universal Broth Batch", field: "ub_batch_number" },
  ];

  useEffect(() => {
    if (workorderPk == null) {
      setWoData(null);
      setLoading(false);
      setError("No work order selected.");
      return () => {};
    }
    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const params = { workorder_pk: workorderPk };
    if (genistaId != null && genistaId !== "") {
      params.genista_id = genistaId;
    }
    getWoInfo(params, controller.signal)
      .then((res) => {
        if (cancelled) return;
        if (res.canceled) return;
        if (res.success && res.data) {
          setWoData(res.data);
        } else {
          const errMsg = res.message || "Failed to load work order details.";
          setError(errMsg);
          toast.error(errMsg);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          const errMsg = err?.response?.data?.message || err?.message || "Failed to load work order details.";
          setError(errMsg);
          toast.error(errMsg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [workorderPk, genistaId]);

  const woInfo = woData ? getWoInfoFromData(woData) : [];
  const tableDataSource = woData ? getTableDataSourceFromData(woData) : [];

  const woStages = mapAndOrderStages(woData?.workorder_stages ?? []);

  const tableColumns = [
    { title: "Genista ID", dataIndex: "genista_id", key: "genista_id" },
    { title: "Sample ID", dataIndex: "sample_id", key: "sample_id" },
    { title: "Sample Type", dataIndex: "sample_type", key: "sample_type" },
    { title: "Test", dataIndex: "analysis", key: "analysis" },
    { title: "Test Category", dataIndex: "test_category", key: "test_category" },
    {
      title: "Result",
      dataIndex: "result",
      key: "result",
      render: (value, record) => (
        <Tooltip
          placement="top"
          title={(
            <span>
              Uploaded Date:
              {" "}
              {record.uploadedDate ?? "-"}
              <br />
              Result Uploaded By:
              {" "}
              {record.resultUploadedBy ?? "-"}
            </span>
          )}
        >
          <span>{value ?? "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "QC",
      dataIndex: "generate_status",
      key: "qc_status",
      width: 140,
      render: (value, record) => {
        const qcConfig = BATCH_REPORT_GENERATE_STATUS[record.generate_status];
        const label = qcConfig?.label ?? "-";
        const style = qcConfig?.style ?? {};
        return (
          <Tooltip
            title={<QcTooltipContent testData={record} />}
            placement="top"
            overlayClassName="WoDetailsModal__RowTooltip"
          >
            <span className="WoDetailsModal__StatusCellContent" style={style}>
              {label}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "test_status",
      key: "status",
      width: 140,
      render: (value, record) => {
        const statusVal = record.test_status ?? record.status;
        const statusConfig = BATCH_REPORT_TEST_STATUS[statusVal];
        const label = statusConfig?.label ?? statusVal ?? "-";
        const style = statusConfig?.style ?? {};
        return (
          <span className="WoDetailsModal__StatusCellContent" style={style}>
            {label}
          </span>
        );
      },
    },
  ];

  const renderInfoSection = (data) => (
    <div className="WoDetailsModal__Section WoDetailsModal__Section--Grid">
      {data.map((item, idx) => {
        const { label, value: fieldValue } = item;
        let value = <p>{fieldValue}</p>;
        if (label.toLowerCase().includes("assigned to") && Array.isArray(fieldValue)) {
          value = (
            <div>
              {fieldValue.map((assignedToValue, assignedToIdx) => (
                <Tooltip title={assignedToIdx === 0 ? "Primary RA" : ""} key={assignedToIdx}>
                  {`${assignedToValue}${(assignedToIdx < fieldValue.length - 1) ? ", " : ""}`}
                </Tooltip>
              ))}
            </div>
          );
        }
        return (
          <div key={idx} className="WoDetailsModal__Description">
            <h6>{label}</h6>
            {value}
          </div>
        );
      })}
    </div>
  );

  const renderStagesSection = useCallback((title, stages, showAdditionalInformation = false) => (
    <div className="WoDetailsModal__Section">
      <div>
        <h6 className="WoDetailsModal__Section__Label">{title}</h6>
        <div className="WoDetailsModal__Stages">
          {stages.map((stage, index) => (
            <span key={index} className="WoDetailsModal__StageItem">
              {stage.checked ? (
                <CheckOutlined className="WoDetailsModal__StageIcon WoDetailsModal__StageIcon--tick" />
              ) : (
                <CloseOutlined className="WoDetailsModal__StageIcon WoDetailsModal__StageIcon--cross" />
              )}
              <span className="WoDetailsModal__StageLabel">{stage.label}</span>
            </span>
          ))}
        </div>
      </div>
      {showAdditionalInformation && (
      <div>
        <h6 className="WoDetailsModal__Section__Label">Additional Information</h6>
        <div className="WoDetailsModal__Stages">
          {additionalInformation.map((batchItem, index) => {
            const { label, field } = batchItem;
            const text = `${label}: ${woData?.[field] || "-"}`;
            return (
              <span key={index} className="WoDetailsModal__StageItem">
                <span className="WoDetailsModal__StageLabel">{text}</span>
              </span>
            );
          })}
        </div>
      </div>
      )}
    </div>
  ), [additionalInformation, woData]);

  const expandedRowRender = useCallback((record) => {
    const {
      belongs_to, notes, composite, commodity, petrifilm, plate, sample_stages, reportable,
    } = record;
    let reportableValue = "-";
    if (Number.isInteger(reportable)) {
      reportableValue = reportable === 1 ? "Yes" : "No";
    }
    const data = [
      { label: "Belongs To", value: belongs_to || "-" },
      { label: "Notes", value: notes || "-" },
      { label: "Composite", value: composite || "-" },
      { label: "Commodity", value: commodity || "-" },
      { label: "Petrifilm", value: petrifilm || "-" },
      { label: "Plate", value: plate || "-" },
      { label: "Reportable and Billable", value: reportableValue },
    ];
    const sampleStages = Array.isArray(sample_stages) && sample_stages.length > 0
      ? mapAndOrderStages(sample_stages)
      : [];
    return (
      <div className="WoDetailsModal__ExpandedCard">
        {renderInfoSection(data)}
        {sampleStages.length > 0 && renderStagesSection("Sample Stages", sampleStages)}
      </div>
    );
  }, [renderStagesSection]);

  return (
    <Modal
      centered
      title="Workorder Details"
      width={1200}
      open
      onCancel={closeModal}
      rootClassName="WoDetailsModal"
      footer={<Button type="primary" onClick={closeModal}>Close</Button>}
    >
      {(() => {
        if (loading) {
          return (
            <div className="WoDetailsModal__BodyContent WoDetailsModal__BodyContent--center">
              <Spin size="large" />
            </div>
          );
        }
        if (error) {
          return (
            <div className="WoDetailsModal__BodyContent WoDetailsModal__BodyContent--center">
              <p className="WoDetailsModal__Error">{error}</p>
            </div>
          );
        }
        return (
          <div className="WoDetailsModal__BodyContent">
            {woInfo.length > 0 && renderInfoSection(woInfo)}
            {woStages.length > 0 && renderStagesSection("WO Stages", woStages, true)}
            <Scrollbar className="WoDetailsModal__TableScrollbar">
              <Table
                rowKey="key"
                columns={tableColumns}
                dataSource={tableDataSource}
                expandable={{
                  expandedRowRender,
                  expandedRowKeys,
                  onExpand: (expanded, record) => {
                    if (expanded) {
                      setExpandedRowKeys([...expandedRowKeys, record.key]);
                    } else {
                      setExpandedRowKeys(expandedRowKeys.filter((k) => k !== record.key));
                    }
                  },
                }}
                rowClassName={(record) => (expandedRowKeys.includes(record.key) ? "WoDetailsModal__Row--expanded" : "")}
                pagination={false}
                bordered
                size="middle"
                sticky
                rootClassName="WoDetailsModal__Table"
              />
            </Scrollbar>
          </div>
        );
      })()}
    </Modal>
  );
}
